import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type AbdominalPainIntensityStringEnum = "0" | "1" | "2" | "3";

type WellBeingStringEnum = "0" | "1" | "2" | "3" | "4";

type AnusSymptomsFrequencyStringEnum = "0" | "1" | "2" | "3" | "4";

type FistulasPainStringEnum = "0" | "1" | "2" | "3" | "4";

type FistulasEmbarrassmentStringEnum = "0" | "1" | "2" | "3" | "4";

type FistulasFlowStringEnum = "0" | "1" | "2" | "3" | "4";

type FistulasPainSkinStringEnum = "0" | "1" | "2" | "3" | "4";

type FistulasMovementStringEnum = "0" | "1" | "2" | "3" | "4";

type ArticularPainSeverityStringEnum = "1" | "2" | "3" | "4";

/**
 * Represents a Crohn survey filled.
 */
export default class CrohnSurveyFilledJournalEvent extends JournalEvent {
  static override type = "crohn-survey-filled-journal-event";

  @attr("number") declare stoolsNumber: number;

  @attr("boolean", {
    allowNull: true
  })
  declare abdominalPain: boolean;

  @attr("string")
  declare abdominalPainIntensity: AbdominalPainIntensityStringEnum;

  @attr("string") declare wellBeing: WellBeingStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare anusSymptoms: boolean;

  @attr("string")
  declare anusSymptomsFrequency: AnusSymptomsFrequencyStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare hasFistulas: boolean;

  @attr("string") declare fistulasPain: FistulasPainStringEnum;

  @attr("string")
  declare fistulasEmbarrassment: FistulasEmbarrassmentStringEnum;

  @attr("string") declare fistulasFlow: FistulasFlowStringEnum;

  @attr("string") declare fistulasPainSkin: FistulasPainSkinStringEnum;

  @attr("string") declare fistulasMovement: FistulasMovementStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare articularPain: boolean;

  @attr("string")
  declare articularPainSeverity: ArticularPainSeverityStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare mouthUlcers: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare skinSymptoms: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptoms: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsRedEye: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsPainful: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsSight: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare fever: boolean;

  @attr("number") declare feverLevel: number;

  @attr("number") declare weight: number;

  @attr("boolean", {
    allowNull: true
  })
  declare repeatedVomiting: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare transitBlockage: boolean;

  @attr("number") declare score: number;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.crohn-survey-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      stoolsNumber: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.stools-number"
        )
      }),
      feverLevel: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fever-level"
        )
      }),
      weight: validateNumber({
        allowBlank: true,
        gt: 0,
        lte: 200,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.weight"
        )
      }),
      score: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.score"
        )
      }),
      abdominalPainIntensity: validateInclusion({
        list: ["0", "1", "2", "3"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.abdominal-pain-intensity"
        )
      }),
      wellBeing: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.well-being"
        )
      }),
      anusSymptomsFrequency: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.anus-symptoms-frequency"
        )
      }),
      fistulasPain: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fistulas-pain"
        )
      }),
      fistulasEmbarrassment: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fistulas-embarrassment"
        )
      }),
      fistulasFlow: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fistulas-flow"
        )
      }),
      fistulasPainSkin: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fistulas-pain-skin"
        )
      }),
      fistulasMovement: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.fistulas-movement"
        )
      }),
      articularPainSeverity: validateInclusion({
        list: ["1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.crohn-survey-filled-journal-event.articular-pain-severity"
        )
      })
    };
  }

  //---- End of generated code ----

  static sections = [
    "stoolsNumber",
    "abdominalPain",
    "abdominalPainIntensity",
    "wellBeing",
    "anusSymptoms",
    "anusSymptomsFrequency",
    "hasFistulas",
    "fistulasPain",
    "fistulasEmbarrassment",
    "fistulasFlow",
    "fistulasPainSkin",
    "fistulasMovement",
    "articularPain",
    "articularPainSeverity",
    "mouthUlcers",
    "skinSymptoms",
    "eyeSymptoms",
    "eyeSymptomsRedEye",
    "eyeSymptomsPainful",
    "eyeSymptomsSight",
    "fever",
    "feverLevel",
    "weight",
    "repeatedVomiting",
    "transitBlockage"
  ];

  @belongsTo("patient", {
    async: false,
    inverse: "crohnSurveyFilledJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "crohn-survey-filled-journal-event": CrohnSurveyFilledJournalEvent;
  }
}
